import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { PoComboOption, PoModalAction, PoModalComponent, PoNotificationService, PoSelectOption } from '@po-ui/ng-components';
import { PgcFilterCriteria, PgcFilterField, PgcSavedFilter, PGC_FILTER_OPERATORS } from '../../models/pgc-filter.model';
import { Utils } from '../../helper/utils';
import { PgcProfileItem } from '../../models/pgc-profile.model';
import { LiteralsService } from '../../services/literals-service';
import { LiteralsWords } from 'src/app/i18n/literals.model';
import { TemporaryStorageService } from '../../services/temporary-storage.service';

@Component({
  selector: 'pgc-filter',
  templateUrl: './pgc-filter.component.html',
  styleUrls: ['./pgc-filter.component.css'],
  standalone: false
})
export class PgcFilterComponent implements OnInit {

  /** Chave da estrutura no sessionStorage (ex: 'structSC1') */
  @Input() structKey: string = '';

  /** Alias da tabela (ex: 'SC1') */
  @Input() tableAlias: string = '';

  /** Chave para salvar os filtros no profile (ex: 'filtersPurchaseRequests') */
  @Input() profileKey: string = '';

  /** Colunas do grid — usadas para filtrar quais campos ficam disponíveis para filtro */
  @Input() gridColumns: Array<any> = [];

  /** Campos a serem excluídos do select de campos disponíveis (ex: ['DHU_STATUS']) */
  @Input() excludeFields: Array<string> = [];

  /** Evento emitido ao aplicar um filtro — retorna a string FILTER para a API */
  @Output() filterApplied = new EventEmitter<string>();

  /** Evento emitido ao limpar o filtro */
  @Output() filterCleared = new EventEmitter<void>();

  @ViewChild('filterModal', { static: true }) filterModal: PoModalComponent;

  literals: LiteralsWords = this.literalsService.getLiterals();
  profile: PgcProfileItem;

  // Campos disponíveis para filtragem
  availableFields: PgcFilterField[] = [];
  fieldOptions: PoSelectOption[] = [];

  // Mapa para lookup rápido de campos por nome (O(1) ao invés de find)
  private fieldsMap: Map<string, PgcFilterField> = new Map();

  // Operadores — inicializado uma vez, reusado por referência
  operatorOptions: PoSelectOption[] = PGC_FILTER_OPERATORS.map(op => ({ value: op.value, label: op.label }));
  private operatorsMap: Map<string, string> = new Map(PGC_FILTER_OPERATORS.map(op => [op.value, op.label]));

  // Formulário de criação de critério
  selectedField: string = '';
  selectedOperator: string = 'eq';
  expressionValue: string = '';
  expressionDateValue: string = '';

  // Tipo do campo selecionado (para alternar entre input texto e datepicker)
  selectedFieldType: string = 'C';

  // Critérios do filtro atual em construção
  currentCriteria: PgcFilterCriteria[] = [];

  // Modo edição — propriedade cacheada para evitar recalcular no change detection
  isEditMode: boolean = false;
  editingFilterName: string = '';
  criteriaFieldsDisabled: boolean = false;

  // Manter filtro ativo (persistido no profile)
  keepFilterActive: boolean = false;

  // Indicador visual de filtro ativo
  hasActiveFilter: boolean = false;
  activeFilterLabel: string = '';

  // Filtros salvos
  savedFilters: PgcSavedFilter[] = [];
  filterNameOptions: PoComboOption[] = [];
  selectedFilterName: string = '';
  newFilterName: string = '';

  // Ações do modal
  primaryAction: PoModalAction = {
    action: () => this.applyFilter(),
    label: this.literals?.str0898
    
  };

  secondaryAction: PoModalAction = {
    action: () => this.clearFilter(),
    label: this.literals?.str0899
  };

  constructor(
    private utils: Utils,
    private poNotification: PoNotificationService,
    private literalsService: LiteralsService,
    private temporaryStorageService: TemporaryStorageService
  ) {}

  ngOnInit(): void {
    this.loadAvailableFields();
    this.loadSavedFilters();
    this.loadActiveFilter();
  }

  /** Abre o modal de filtros */
  openModal(): void {
    this.filterModal.open();
  }

  /** Carrega os campos disponíveis com base na estrutura da tabela, limitados às colunas do grid */
  loadAvailableFields(): void {
    const struct = this.temporaryStorageService.getValue(this.structKey);

    if (struct?.[this.tableAlias]?.['fields']) {
      const allFields = struct[this.tableAlias]['fields'].filter(f => f.virtual === false);

      // Obtém as properties do grid para filtrar apenas os campos presentes nas colunas
      const gridProperties = new Set(this.gridColumns.map(col => col.property?.toLowerCase()));
      const excludedFields = new Set(this.excludeFields.map(f => f.toLowerCase()));

      let filteredFields = gridProperties.size > 0
        ? allFields.filter(f => gridProperties.has(f.field.toLowerCase()))
        : allFields;

      // Remove campos excluídos manualmente
      if (excludedFields.size > 0) {
        filteredFields = filteredFields.filter(f => !excludedFields.has(f.field.toLowerCase()));
      }

      this.availableFields = filteredFields.map(field => ({
        field: field.field,
        title: field.title,
        type: field.type,
        size: field.size,
        decimal: field.decimal || 0
      }));

      // Constrói mapa para lookup O(1)
      this.fieldsMap.clear();
      this.availableFields.forEach(f => this.fieldsMap.set(f.field.toLowerCase(), f));

      this.fieldOptions = this.availableFields.map(f => ({
        value: f.field.toLowerCase(),
        label: `${f.title} (${f.field})`
      }));
    }
  }

  /** Carrega filtros salvos do profile */
  loadSavedFilters(): void {
    this.profile = this.utils.getProfile();
    const stored = this.profile[this.profileKey];
    this.savedFilters = Array.isArray(stored) ? stored : [];
    this.updateFilterNameOptions();
  }

  /** Carrega o filtro ativo do profile e aplica automaticamente */
  private loadActiveFilter(): void {
    this.profile = this.utils.getProfile();
    const activeFilterKey = `${this.profileKey}_active`;
    const activeFilterName = this.profile[activeFilterKey];

    if (activeFilterName) {
      const filter = this.savedFilters.find(f => f.name === activeFilterName);
      if (filter) {
        this.keepFilterActive = true;
        this.currentCriteria = [...filter.criteria];
        this.setSelectedFilter(activeFilterName);
        this.setActiveFilterIndicator(true);
        // Não emite filterApplied aqui — o parent já carrega com o filtro ativo no ngOnInit
      } else {
        // Filtro salvo foi removido — limpa a referência do profile
        this.keepFilterActive = false;
        delete this.profile[activeFilterKey];
        this.utils.updateProfile(this.profile);
      }
    }
  }

  /** Handler do switch 'Manter filtro ativo' */
  handleKeepFilterActiveChange(): void {
    if (this.keepFilterActive && !this.selectedFilterName) {
      this.keepFilterActive = false;
      this.poNotification.warning(this.literals?.str0900);
      return;
      
    }

    this.poNotification.success(this.literals?.str0901);
    this.persistActiveFilter();
  }

  /** Atualiza opções do combo de filtros salvos */
  updateFilterNameOptions(): void {
    this.filterNameOptions = this.savedFilters.map(f => ({
      value: f.name,
      label: f.name
    }));
  }

  /** Adiciona um critério ao filtro atual */
  addCriteria(): void {
    if (!this.selectedField) {
      this.poNotification.warning(this.literals?.str0902);
      return;
    }
    if (!this.selectedOperator) {
      this.poNotification.warning(this.literals?.str0903);
      return;
    }

    const isDateField = this.selectedFieldType === 'D';
    const value = isDateField ? this.expressionDateValue : this.expressionValue;

    if (!value && value !== '0') {
      this.poNotification.warning(this.literals?.str0904);
      return;
    }

    const fieldInfo = this.fieldsMap.get(this.selectedField);
    const operatorLabel = this.operatorsMap.get(this.selectedOperator) || this.selectedOperator;

    // Para campos tipo data, converte de YYYY-MM-DD para YYYYMMDD (formato Protheus)
    const finalValue = (isDateField && value) ? value.replace(/-/g, '') : value;

    this.currentCriteria = [...this.currentCriteria, {
      field: this.selectedField,
      fieldLabel: fieldInfo?.title || this.selectedField,
      operator: this.selectedOperator,
      operatorLabel: operatorLabel,
      value: finalValue
    }];

    // Limpa campos do formulário
    this.selectedField = '';
    this.selectedOperator = 'eq';
    this.expressionValue = '';
    this.expressionDateValue = '';
    this.selectedFieldType = 'C';
  }

  /** Detecta o tipo do campo selecionado */
  onFieldChange(fieldValue: string): void {
    this.selectedFieldType = fieldValue ? (this.fieldsMap.get(fieldValue)?.type || 'C') : 'C';
    this.expressionValue = '';
    this.expressionDateValue = '';
  }

  /** Remove um critério pelo índice */
  removeCriteria(index: number): void {
    this.currentCriteria = this.currentCriteria.filter((_, i) => i !== index);
  }

  /** Limpa todos os critérios */
  clearCriteria(): void {
    this.currentCriteria = [];
  }

  /** Aplica o filtro montado — emite a string FILTER para a API e fecha o modal */
  applyFilter(): void {
    if (this.currentCriteria.length === 0) {
      this.poNotification.warning(this.literals?.str0905);
      return;
    }

    this.filterApplied.emit(this.buildFilterString());
    this.setActiveFilterIndicator(true);
    this.filterModal.close();
  }

  /** Limpa o filtro e emite evento */
  clearFilter(): void {
    this.currentCriteria = [];
    this.setSelectedFilter('');
    this.exitEditMode();
    this.setActiveFilterIndicator(false);

    if (this.keepFilterActive) {
      this.keepFilterActive = false;
      this.persistActiveFilter();
    }

    this.filterCleared.emit();
    this.filterModal.close();
  }

  /** Salva o filtro atual com um nome */
  saveFilter(): void {
    const name = this.newFilterName?.trim();
    if (!name) {
      this.poNotification.warning(this.literals?.str0906);
      return;
    }
    if (this.currentCriteria.length === 0) {
      this.poNotification.warning(this.literals?.str0907);
      return;
    }

    const existingIndex = this.savedFilters.findIndex(f => f.name === name);
    const filterToSave: PgcSavedFilter = {
      name: name,
      criteria: [...this.currentCriteria]
    };

    if (existingIndex >= 0) {
      this.savedFilters[existingIndex] = filterToSave;
    } else {
      this.savedFilters.push(filterToSave);
    }

    this.persistFilters();
    this.updateFilterNameOptions();
    this.newFilterName = '';
    this.poNotification.success(`${this.literals?.str0908} "${name}" ${this.literals?.str0909}`);
    
  }

  /** Carrega um filtro salvo pelo nome selecionado */
  loadFilter(filterName: string): void {
    if (!filterName) {
      this.currentCriteria = [];
      this.setSelectedFilter('');
      this.exitEditMode();
      // Desativa o switch ao limpar seleção
      if (this.keepFilterActive) {
        this.keepFilterActive = false;
        this.persistActiveFilter();
      }
      return;
    }

    const filter = this.savedFilters.find(f => f.name === filterName);
    if (filter) {
      this.currentCriteria = [...filter.criteria];
      this.setSelectedFilter(filterName);
    }
  }

  /** Remove um filtro salvo */
  deleteFilter(): void {
    if (!this.selectedFilterName) {
      this.poNotification.warning(this.literals?.str0910);
      return;
    }

    const deletedName = this.selectedFilterName;
    this.savedFilters = this.savedFilters.filter(f => f.name !== deletedName);
    this.persistFilters();
    this.updateFilterNameOptions();
    this.currentCriteria = [];
    this.setSelectedFilter('');
    this.exitEditMode();

    // Se o filtro excluído era o ativo, limpa a persistência
    if (this.keepFilterActive) {
      this.keepFilterActive = false;
      this.persistActiveFilter();
    }

    this.poNotification.success(this.literals?.str0911);
  }

  /** Entra no modo edição do filtro selecionado */
  editFilter(): void {
    if (!this.selectedFilterName) {
      this.poNotification.warning(this.literals?.str0912);
      return;
    }

    const filter = this.savedFilters.find(f => f.name === this.selectedFilterName);
    if (filter) {
      this.currentCriteria = [...filter.criteria];
      this.isEditMode = true;
      this.editingFilterName = filter.name;
      this.newFilterName = filter.name;
      this.updateCriteriaFieldsState();
    }
  }

  /** Salva as alterações do filtro em edição */
  saveEditedFilter(): void {
    if (this.currentCriteria.length === 0) {
      this.poNotification.warning(this.literals?.str0913);
      return;
    }

    const existingIndex = this.savedFilters.findIndex(f => f.name === this.editingFilterName);
    if (existingIndex >= 0) {
      this.savedFilters[existingIndex] = {
        name: this.editingFilterName,
        criteria: [...this.currentCriteria]
      };
    }

    this.persistFilters();
    this.updateFilterNameOptions();
    this.poNotification.success(`Filtro "${this.editingFilterName}" atualizado com sucesso.`);
    this.exitEditMode();
  }

  /** Cancela o modo edição e restaura o estado anterior */
  cancelEdit(): void {
    this.exitEditMode();
    this.currentCriteria = [];
    this.setSelectedFilter('');
  }

  /** Persiste os filtros no profile */
  private persistFilters(): void {
    this.profile = this.utils.getProfile();
    this.profile[this.profileKey] = this.savedFilters;
    this.utils.updateProfile(this.profile);
  }

  /** Persiste ou remove o filtro ativo no profile */
  private persistActiveFilter(): void {
    this.profile = this.utils.getProfile();
    const activeFilterKey = `${this.profileKey}_active`;

    if (this.keepFilterActive && this.selectedFilterName) {
      this.profile[activeFilterKey] = this.selectedFilterName;
    } else {
      delete this.profile[activeFilterKey];
    }

    this.utils.updateProfile(this.profile);
  }

  /** Atualiza o estado de desabilitação dos campos de critério */
  private updateCriteriaFieldsState(): void {
    this.criteriaFieldsDisabled = !!this.selectedFilterName && !this.isEditMode;
  }

  /** Define o filtro selecionado e atualiza estado dos campos */
  private setSelectedFilter(name: string): void {
    this.selectedFilterName = name;
    this.updateCriteriaFieldsState();
  }

  /** Sai do modo edição e atualiza estado dos campos */
  private exitEditMode(): void {
    this.isEditMode = false;
    this.editingFilterName = '';
    this.newFilterName = '';
    this.updateCriteriaFieldsState();
  }

  /** Atualiza o indicador visual de filtro ativo */
  private setActiveFilterIndicator(active: boolean): void {
    this.hasActiveFilter = active;
    if (active) {
      this.activeFilterLabel = `${this.currentCriteria.length}`;
    } else {
      this.activeFilterLabel = '';
    }
  }

  /** Monta a string FILTER para a API
   *  Formato: campo op valor [and campo op valor ...]
   *  Strings são envoltas em aspas simples
   */
  private buildFilterString(): string {
    return this.currentCriteria.map(c => {
      const fieldInfo = this.fieldsMap.get(c.field);
      const isNumeric = fieldInfo?.type === 'N';
      return isNumeric ? `${c.field} ${c.operator} ${c.value}` : `${c.field} ${c.operator} '${c.value}'`;
    }).join(' and ');
  }
}
